// Typed Firestore helpers. Features never import from 'firebase/firestore' directly.
// This module is the single abstraction point for all Firestore access.

import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc as firestoreAddDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot as firestoreOnSnapshot,
  writeBatch as firestoreWriteBatch,
  arrayUnion,
  deleteField,
  serverTimestamp,
  Timestamp,
  type Firestore,
  type DocumentData,
  type QueryConstraint,
  type WithFieldValue,
  type PartialWithFieldValue,
  type DocumentReference,
  type CollectionReference,
  type QueryDocumentSnapshot,
  type Query,
  type DocumentSnapshot,
  type QuerySnapshot,
  type FirestoreError,
  type Unsubscribe,
  type WriteBatch,
} from 'firebase/firestore'
import { app } from './app'
import { toUserFacingError } from '@/lib/errors'

const db: Firestore = getFirestore(app)

/** Returns a strongly-typed CollectionReference<T> for the given path. */
function typedCollection<T extends DocumentData>(path: string): CollectionReference<T> {
  return collection(db, path) as CollectionReference<T>
}

/** Returns a strongly-typed DocumentReference<T> for the given path segments. */
function typedDoc<T extends DocumentData>(
  path: string,
  ...pathSegments: string[]
): DocumentReference<T> {
  return doc(db, path, ...pathSegments) as DocumentReference<T>
}

/** Creates a typed document reference with a Firestore-generated ID. */
function newTypedDoc<T extends DocumentData>(ref: CollectionReference<T>): DocumentReference<T> {
  return doc(ref)
}

/** Fetches a single document, returning null if it does not exist. */
async function getDocument<T extends DocumentData>(ref: DocumentReference<T>): Promise<T | null> {
  try {
    const snap = await getDoc(ref)
    return snap.exists() ? snap.data() : null
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not load this information. Please try again.',
      'firestore/get-document'
    )
  }
}

/** Fetches all documents in a collection, with optional query constraints. */
async function getCollection<T extends DocumentData>(
  ref: CollectionReference<T>,
  ...constraints: QueryConstraint[]
): Promise<Array<T & { id: string }>> {
  try {
    const q = constraints.length > 0 ? query(ref, ...constraints) : ref
    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not load these records. Please try again.',
      'firestore/get-collection'
    )
  }
}

export interface CollectionPage<T extends DocumentData> {
  items: Array<T & { id: string }>
  lastDoc: QueryDocumentSnapshot<T> | null
  hasMore: boolean
}

/** Fetches a page of documents using cursor-based pagination. Pass null cursor for the first page. */
async function getCollectionPage<T extends DocumentData>(
  ref: CollectionReference<T>,
  pageSize: number,
  cursor: QueryDocumentSnapshot<T> | null,
  ...constraints: QueryConstraint[]
): Promise<CollectionPage<T>> {
  try {
    const allConstraints: QueryConstraint[] = [
      ...constraints,
      ...(cursor ? [startAfter(cursor)] : []),
      limit(pageSize + 1),
    ]
    const snap = await getDocs(query(ref, ...allConstraints))
    const hasMore = snap.docs.length > pageSize
    const docs = hasMore ? snap.docs.slice(0, pageSize) : snap.docs
    return {
      items: docs.map((d) => ({ id: d.id, ...d.data() })),
      lastDoc: docs.length > 0 ? (docs[docs.length - 1] as QueryDocumentSnapshot<T>) : null,
      hasMore,
    }
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not load more records. Please try again.',
      'firestore/get-collection-page'
    )
  }
}

async function addDoc<T extends DocumentData>(
  ref: CollectionReference<T>,
  data: WithFieldValue<T>
): Promise<DocumentReference<T>> {
  try {
    return await firestoreAddDoc(ref, data)
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not save this information. Please try again.',
      'firestore/create-document'
    )
  }
}

async function setDocument<T extends DocumentData>(
  ref: DocumentReference<T>,
  data: WithFieldValue<T>
): Promise<void> {
  try {
    await setDoc(ref, data)
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not save this information. Please try again.',
      'firestore/set-document'
    )
  }
}

/** Sets a document with merge semantics, preserving a safe error at the boundary. */
async function setDocumentMerge<T extends DocumentData>(
  ref: DocumentReference<T>,
  data: PartialWithFieldValue<T>
): Promise<void> {
  try {
    await setDoc(ref, data as WithFieldValue<T>, { merge: true })
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not save these settings. Please try again.',
      'firestore/set-document-merge'
    )
  }
}

async function updateDocument<T extends DocumentData>(
  ref: DocumentReference<T>,
  data: PartialWithFieldValue<T>
): Promise<void> {
  try {
    await updateDoc(ref, data as PartialWithFieldValue<DocumentData>)
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not save these changes. Please try again.',
      'firestore/update-document'
    )
  }
}

async function deleteDocument<T extends DocumentData>(ref: DocumentReference<T>): Promise<void> {
  try {
    await deleteDoc(ref)
  } catch (error) {
    throw toUserFacingError(
      error,
      'We could not remove this information. Please try again.',
      'firestore/delete-document'
    )
  }
}

function writeBatch(database: Firestore): WriteBatch {
  const batch = firestoreWriteBatch(database)
  const commit = batch.commit.bind(batch)
  batch.commit = () =>
    commit().catch((error: unknown) => {
      throw toUserFacingError(
        error,
        'We could not save all of these changes. Please try again.',
        'firestore/commit-batch'
      )
    })
  return batch
}

function onSnapshot<T extends DocumentData>(
  reference: DocumentReference<T>,
  onNext: (snapshot: DocumentSnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
  onCompletion?: () => void,
  options?: { includeMetadataChanges: boolean }
): Unsubscribe
function onSnapshot<T extends DocumentData>(
  reference: Query<T>,
  onNext: (snapshot: QuerySnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
  onCompletion?: () => void,
  options?: { includeMetadataChanges: boolean }
): Unsubscribe
function onSnapshot(
  reference: unknown,
  onNext: (...args: never[]) => void,
  onError?: (...args: never[]) => void,
  onCompletion?: () => void,
  options?: { includeMetadataChanges: boolean }
): Unsubscribe {
  return (firestoreOnSnapshot as unknown as (...args: unknown[]) => Unsubscribe)(
    reference,
    ...(options ? [options] : []),
    onNext,
    (error: unknown) => {
      onError?.(
        toUserFacingError(
          error,
          'We could not keep this information up to date. Please try again.',
          'firestore/subscribe'
        ) as unknown as never
      )
    },
    onCompletion
  )
}

export {
  db,
  typedCollection,
  typedDoc,
  newTypedDoc,
  getDocument,
  getCollection,
  getCollectionPage,
  setDocument,
  setDocumentMerge,
  updateDocument,
  deleteDocument,
  // Re-export query utilities so features never import firebase/firestore directly
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  writeBatch,
  arrayUnion,
  deleteField,
  serverTimestamp,
  Timestamp,
  type QueryDocumentSnapshot,
}
